import { useEffect, useMemo, useState } from "react";

import { colors, radii, spacing } from "../../../../styles/design-system";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";
import { CheckIcon, NotePencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";

import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import DeleteQuestionConfirmationModal from "./DeleteQuestionConfirmationModal";
import McqQuestionEditorModal from "./McqQuestionEditorModal";
import SocraticQuestionEditorModal from "./SocraticQuestionEditorModal";
import { useToast } from "../../../../contexts/Toast/ToastProvider";

import {
  AssessmentType,
  useCreateMcqQuestionMutation,
  useCreateSocraticQuestionMutation,
  useDeleteMcqQuestionMutation,
  useDeleteSocraticQuestionMutation,
} from "@axio/graphql/generated/graphql";
import type { AiTutor, McqQuestion, Question } from "@axio/graphql/generated/graphql";

type EditorQuestion = McqQuestion | Question;
type SocraticQuestionEntry = {
  tutor: AiTutor;
  question: Question;
};

type PendingDeleteAction = {
  mode: "bulk";
  questionType: AssessmentType;
  questionIds: string[];
};

type DeletedQuestionSnapshot =
  | {
      questionType: AssessmentType.Mcq;
      questionId: string;
      question: McqQuestion;
    }
  | {
      questionType: AssessmentType.SocraticDialog;
      questionId: string;
      question: Question;
      tutorId: string;
    };

export type AssessmentQuestionBankModalProps = {
  visible: boolean;
  onClose: () => void;
  isLoading: boolean;
  errorMessage?: string | null;
  assessmentId?: string;
  assessmentType?: AssessmentType | null;
  aiTutors: AiTutor[];
  mcqQuestions?: McqQuestion[];
  onRefresh?: () => Promise<void> | void;
  pageSize?: number;
  socraticPage?: number;
  mcqPage?: number;
  socraticTotalCount?: number;
  mcqTotalCount?: number;
  onPageChange?: (params: { socraticPage?: number; mcqPage?: number }) => Promise<void> | void;
  submitText?: string;
  onPrimaryAction?: () => Promise<void> | void;
  numberOfQuestions?: number;
  onPoolConstraintViolation?: (message: string) => void;
};

const optionLabel = (index: number) => String.fromCharCode(65 + index);
const AssessmentQuestionBankModal = ({
  visible,
  onClose,
  isLoading,
  errorMessage,
  assessmentId,
  assessmentType,
  aiTutors,
  mcqQuestions = [],
  onRefresh,
  pageSize = 25,
  socraticPage = 1,
  mcqPage = 1,
  socraticTotalCount,
  mcqTotalCount,
  onPageChange,
  submitText = "Back",
  onPrimaryAction,
  numberOfQuestions,
  onPoolConstraintViolation,
}: AssessmentQuestionBankModalProps) => {
  const { showToast } = useToast();
  const [createMcqQuestion] = useCreateMcqQuestionMutation();
  const [createSocraticQuestion] = useCreateSocraticQuestionMutation();
  const [deleteMcqQuestion, { loading: isDeletingQuestion }] = useDeleteMcqQuestionMutation();
  const [deleteSocraticQuestion, { loading: isDeletingSocraticQuestion }] = useDeleteSocraticQuestionMutation();
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorType, setEditorType] = useState<"mcq" | "socratic">("mcq");
  const [editingQuestion, setEditingQuestion] = useState<EditorQuestion | null>(null);
  const [editingSocraticQuestionAiTutorId, setEditingSocraticQuestionAiTutorId] = useState<string | null>(null);
  const [isQuestionEditorOpen, setIsQuestionEditorOpen] = useState(false);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [pendingDeleteAction, setPendingDeleteAction] = useState<PendingDeleteAction | null>(null);
  const currentAiTutors = aiTutors;
  const currentMcqQuestions = mcqQuestions;
  const isMcqAssessment = assessmentType === AssessmentType.Mcq;
  const currentQuestionType: AssessmentType = isMcqAssessment ? AssessmentType.Mcq : AssessmentType.SocraticDialog;
  const canManageQuestions = Boolean(assessmentId);
  const flattenedSocraticQuestions = useMemo<SocraticQuestionEntry[]>(() => {
    return currentAiTutors.flatMap((tutor) =>
      (tutor.questions ?? [])
        .filter((question) => question && question.isDeleted !== true)
        .filter(Boolean)
        .map((question) => ({ tutor, question }))
    );
  }, [currentAiTutors]);

  const totalQuestions = isMcqAssessment ? currentMcqQuestions.length : flattenedSocraticQuestions.length;
  const totalQuestionsInBank = isMcqAssessment
    ? (mcqTotalCount ?? totalQuestions)
    : (socraticTotalCount ?? totalQuestions);
  const currentPage = isMcqAssessment ? mcqPage : socraticPage;
  const totalPages = Math.max(1, Math.ceil(Math.max(0, totalQuestionsInBank) / Math.max(1, pageSize)));
  const canLoadPreviousPage = currentPage > 1;
  const canLoadNextPage = currentPage < totalPages;
  const loadedLabel = `${totalQuestions} loaded of ${totalQuestionsInBank}`;
  const totalIles = currentAiTutors.length;
  const questionLabel = `question${totalQuestions === 1 ? "" : "s"}`;
  const ileLabel = `ILE${totalIles === 1 ? "" : "s"}`;
  const questionBankSummaryText = `${loadedLabel} ${questionLabel} in bank from ${totalIles} ${ileLabel}`;
  const configuredQuestionCount =
    typeof numberOfQuestions === "number" && Number.isFinite(numberOfQuestions)
      ? Math.max(0, Math.trunc(numberOfQuestions))
      : 0;
  const currentQuestionIds = useMemo(() => {
    if (isMcqAssessment) {
      return currentMcqQuestions
        .map((question) => question.id)
        .filter((questionId): questionId is string => Boolean(questionId));
    }

    return flattenedSocraticQuestions
      .map(({ question }) => question.id)
      .filter((questionId): questionId is string => Boolean(questionId));
  }, [isMcqAssessment, currentMcqQuestions, flattenedSocraticQuestions]);
  const allCurrentQuestionsSelected =
    currentQuestionIds.length > 0 && currentQuestionIds.every((questionId) => selectedQuestionIds.includes(questionId));
  const selectedQuestionCount = selectedQuestionIds.length;
  const selectableQuestionCount = currentQuestionIds.length;

  const nextQuestionOrder = useMemo(() => {
    return currentMcqQuestions.length + 1;
  }, [currentMcqQuestions]);

  const closeQuestionEditor = () => setIsQuestionEditorOpen(false);

  const handleOpenQuestionEditor = (
    type: "mcq" | "socratic",
    mode: "create" | "edit",
    question?: EditorQuestion,
    selectedTutorId?: string | null
  ) => {
    setEditorMode(mode);
    setEditorType(type);
    setEditingQuestion(question ?? null);
    setEditingSocraticQuestionAiTutorId(selectedTutorId ?? (type === "mcq" ? null : (currentAiTutors[0]?.id ?? null)));
    setIsQuestionEditorOpen(true);
  };

  const handleOpenCreateQuestionModal = () => {
    handleOpenQuestionEditor(isMcqAssessment ? "mcq" : "socratic", "create");
  };

  const handleOpenEditQuestionModal = (question: McqQuestion) => {
    handleOpenQuestionEditor("mcq", "edit", question);
  };

  const handleOpenEditSocraticQuestionModal = (question: Question, tutor: AiTutor) => {
    handleOpenQuestionEditor("socratic", "edit", question, tutor.id ?? null);
  };

  const isDeletingAnyQuestion = isDeletingQuestion || isDeletingSocraticQuestion;

  useEffect(() => {
    if (!visible) {
      setSelectedQuestionIds([]);
      setPendingDeleteAction(null);
      return;
    }

    setSelectedQuestionIds([]);
  }, [visible, currentPage, isMcqAssessment]);

  const ensurePoolConstraint = (deleteCount: number) => {
    if (totalQuestionsInBank - deleteCount >= configuredQuestionCount) {
      return true;
    }

    const questionWord = deleteCount === 1 ? "question" : "questions";
    const message = `Cannot delete ${deleteCount} ${questionWord} because the pool would drop below the configured number of questions (${configuredQuestionCount}).`;
    showToast({
      message,
      type: "warning",
    });
    onPoolConstraintViolation?.(message);
    return false;
  };

  const deleteQuestionById = async (questionType: AssessmentType, questionId: string) => {
    if (!questionId) {
      return { success: false, message: "Question ID is missing." };
    }

    if (!assessmentId) {
      return { success: false, message: "Assessment ID is missing." };
    }

    try {
      if (questionType === AssessmentType.Mcq) {
        const { data } = await deleteMcqQuestion({
          variables: {
            mcqQuestionId: questionId,
            assessmentId,
          },
        });

        const response = data?.deleteMcqQuestion;
        if (!response?.success) {
          return {
            success: false,
            message: response?.message || "Failed to delete question.",
          };
        }
      } else {
        const { data } = await deleteSocraticQuestion({
          variables: {
            questionId,
            assessmentId,
          },
        });

        const response = data?.deleteSocraticQuestion;
        if (!response?.success) {
          return {
            success: false,
            message: response?.message || "Failed to delete question.",
          };
        }
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : "Failed to delete question.",
      };
    }
  };

  const createDeletedQuestionSnapshot = (
    questionType: AssessmentType,
    questionId: string
  ): DeletedQuestionSnapshot | null => {
    if (questionType === AssessmentType.Mcq) {
      const deletedMcqQuestion = currentMcqQuestions.find((question) => question.id === questionId);
      if (!deletedMcqQuestion) {
        return null;
      }

      return {
        questionType,
        questionId,
        question: deletedMcqQuestion,
      };
    }

    const deletedSocraticQuestion = flattenedSocraticQuestions.find(({ question }) => question.id === questionId);
    if (!deletedSocraticQuestion || !deletedSocraticQuestion.tutor.id) {
      return null;
    }

    return {
      questionType,
      questionId,
      question: deletedSocraticQuestion.question,
      tutorId: deletedSocraticQuestion.tutor.id,
    };
  };

  const handleUndoDelete = async (snapshot: DeletedQuestionSnapshot) => {
    if (!assessmentId) {
      showToast({
        message: "Unable to undo deletion: assessment context is missing.",
        type: "error",
      });
      return;
    }

    try {
      if (snapshot.questionType === AssessmentType.Mcq) {
        const mcqQuestion = snapshot.question;
        const correctChoiceIndices = (mcqQuestion as { correctChoiceIndices?: number[] }).correctChoiceIndices ?? [];
        const response = await createMcqQuestion({
          variables: {
            input: {
              assessmentId,
              aiTutorId:
                (mcqQuestion as { aiTutorId?: string | null; aiTutor?: { id?: string | null } | null }).aiTutorId ??
                (mcqQuestion as { aiTutor?: { id?: string | null } | null }).aiTutor?.id ??
                undefined,
              question: mcqQuestion.question ?? "",
              choices: mcqQuestion.choices ?? [],
              correctChoiceIndices,
              explanation: mcqQuestion.explanation ?? "",
              totalScore: mcqQuestion.totalScore ?? undefined,
              order: mcqQuestion.order ?? undefined,
            },
          },
        });

        if (!response.data?.createMcqQuestion?.success) {
          showToast({
            message: response.data?.createMcqQuestion?.message || "Failed to restore deleted question.",
            type: "error",
          });
          return;
        }
      } else {
        const socraticQuestion = snapshot.question;
        const response = await createSocraticQuestion({
          variables: {
            input: {
              assessmentId,
              aiTutorId: snapshot.tutorId,
              question: socraticQuestion.question ?? "",
              rubrics: (socraticQuestion.rubrics ?? []).map((rubric) => ({
                component: rubric.component ?? "",
                points: rubric.points ?? 0,
              })),
              totalScore: socraticQuestion.totalScore ?? undefined,
            },
          },
        });

        if (!response.data?.createSocraticQuestion?.success) {
          showToast({
            message: response.data?.createSocraticQuestion?.message || "Failed to restore deleted question.",
            type: "error",
          });
          return;
        }
      }

      await onRefresh?.();
      showToast({
        message: "Deletion undone successfully.",
        type: "success",
      });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Failed to restore deleted question.",
        type: "error",
      });
    }
  };

  const handleDeleteQuestion = async (questionType: AssessmentType, questionId: string) => {
    if (!questionId || isDeletingAnyQuestion) {
      return;
    }

    const deletedQuestionSnapshot = createDeletedQuestionSnapshot(questionType, questionId);

    if (!ensurePoolConstraint(1)) {
      return;
    }

    const result = await deleteQuestionById(questionType, questionId);

    if (result.success) {
      await onRefresh?.();
      showToast({
        message: "Question deleted successfully.",
        type: "success",
        visibleDuration: 7000,
        actions: deletedQuestionSnapshot
          ? [
              {
                actionText: "Undo",
                actionCallback: () => {
                  void handleUndoDelete(deletedQuestionSnapshot);
                },
              },
            ]
          : undefined,
      });
      return;
    }

    showToast({
      message: result.message ?? "Failed to delete question.",
      type: "error",
    });
  };

  const toggleQuestionSelection = (questionId: string) => {
    setSelectedQuestionIds((previousIds) => {
      if (previousIds.includes(questionId)) {
        return previousIds.filter((id) => id !== questionId);
      }

      return [...previousIds, questionId];
    });
  };

  const toggleSelectAllQuestions = () => {
    if (allCurrentQuestionsSelected) {
      setSelectedQuestionIds([]);
      return;
    }

    setSelectedQuestionIds(currentQuestionIds);
  };

  const clearSelectedQuestions = () => {
    setSelectedQuestionIds([]);
  };

  const handleOpenBulkDeleteConfirmation = () => {
    if (selectedQuestionIds.length === 0 || isDeletingAnyQuestion) {
      return;
    }

    setPendingDeleteAction({
      mode: "bulk",
      questionType: currentQuestionType,
      questionIds: selectedQuestionIds,
    });
  };

  const closeDeleteConfirmation = () => setPendingDeleteAction(null);

  const handleConfirmDelete = async () => {
    if (!pendingDeleteAction) {
      return;
    }

    const { questionType, questionIds } = pendingDeleteAction;
    if (!ensurePoolConstraint(questionIds.length)) {
      closeDeleteConfirmation();
      return;
    }

    closeDeleteConfirmation();

    let successCount = 0;
    let firstErrorMessage: string | null = null;

    for (const questionId of questionIds) {
      const result = await deleteQuestionById(questionType, questionId);
      if (result.success) {
        successCount += 1;
      } else if (!firstErrorMessage) {
        firstErrorMessage = result.message ?? "Failed to delete question.";
      }
    }

    if (successCount > 0) {
      await onRefresh?.();
    }

    if (successCount === questionIds.length) {
      showToast({
        message: `${successCount} questions deleted successfully.`,
        type: "success",
      });
    } else if (successCount > 0) {
      showToast({
        message: `${successCount} questions deleted. ${questionIds.length - successCount} failed.`,
        type: "warning",
      });
      if (firstErrorMessage) {
        showToast({
          message: firstErrorMessage,
          type: "error",
        });
      }
    } else {
      showToast({
        message: firstErrorMessage ?? "Failed to delete selected questions.",
        type: "error",
      });
    }

    setSelectedQuestionIds([]);
  };

  const socraticQuestionForEditor = editorType === "socratic" ? (editingQuestion as Question | null) : null;
  const mcqQuestionForEditor =
    editorType === "mcq"
      ? ((editingQuestion as
          | (McqQuestion & { aiTutorId?: string | null; aiTutor?: { id?: string | null } | null })
          | null) ?? null)
      : null;
  const mcqEditorSelectedAiTutorId = mcqQuestionForEditor?.aiTutorId ?? mcqQuestionForEditor?.aiTutor?.id ?? null;
  const canShowCreateQuestionButton = canManageQuestions;
  const handlePrimaryAction = async () => {
    if (onPrimaryAction) {
      await onPrimaryAction();
      return;
    }

    onClose();
  };

  const handleSubmit = async () => {
    if (!onPrimaryAction) {
      onClose();
      return;
    }

    await handlePrimaryAction();
  };

  const handlePreviousPage = async () => {
    if (!onPageChange || !canLoadPreviousPage) {
      return;
    }

    if (isMcqAssessment) {
      await onPageChange({ mcqPage: currentPage - 1 });
      return;
    }

    await onPageChange({ socraticPage: currentPage - 1 });
  };

  const handleNextPage = async () => {
    if (!onPageChange || !canLoadNextPage) {
      return;
    }

    if (isMcqAssessment) {
      await onPageChange({ mcqPage: currentPage + 1 });
      return;
    }

    await onPageChange({ socraticPage: currentPage + 1 });
  };

  return (
    <CustomModal visible={visible} onRequestClose={onClose} ariaLabel="Assessment Question Bank">
      <FormLayout
        title="Question Bank"
        isSubmitting={false}
        onCancel={onClose}
        onSubmit={handleSubmit}
        actionText={submitText}
        hideCancelButton={true}
        buttonsContainerStyle={{ justifyContent: "flex-end" }}
        contentContainerStyle={{ paddingBottom: 0 }}>
        {isLoading ? (
          <View style={styles.loading}>
            <ActivityIndicator size="large" color={colors.brand[500]} />
          </View>
        ) : (
          <>
            {!!errorMessage && (
              <>
                <CustomText text={errorMessage} useTranslationText={false} style={styles.errorText} />
                <CustomSpacing size="m" />
              </>
            )}

            {canShowCreateQuestionButton && (
              <>
                <View style={styles.actionsRow}>
                  <CustomButton
                    text="Add Question"
                    useTranslationText={false}
                    styleType="primaryTransparentStroke"
                    size="s"
                    leftIcon={<PlusIcon size={16} color={colors.brand[500]} />}
                    rightIcon={null}
                    onPress={handleOpenCreateQuestionModal}
                  />
                </View>
                <CustomSpacing size="m" />
              </>
            )}

            {selectedQuestionCount > 0 ? (
              <View style={styles.selectionSummaryPill}>
                <CustomText
                  text={`${selectedQuestionCount} of ${selectableQuestionCount} selected`}
                  size="s"
                  useTranslationText={false}
                  style={styles.selectionSummaryText}
                  weight="semiBold"
                />
                <View style={styles.selectionSummaryActions}>
                  <CustomButton
                    text="Select all"
                    useTranslationText={false}
                    styleType="primaryTransparentStroke"
                    size="s"
                    leftIcon={null}
                    rightIcon={null}
                    onPress={toggleSelectAllQuestions}
                    disabled={selectableQuestionCount === 0 || allCurrentQuestionsSelected || isDeletingAnyQuestion}
                  />
                  <CustomButton
                    text="Clear"
                    useTranslationText={false}
                    styleType="primaryTransparentStroke"
                    size="s"
                    leftIcon={null}
                    rightIcon={null}
                    onPress={clearSelectedQuestions}
                    disabled={selectedQuestionCount === 0 || isDeletingAnyQuestion}
                  />
                  <TouchableOpacity
                    style={[
                      styles.bulkDeleteActionButton,
                      (selectedQuestionCount === 0 || isDeletingAnyQuestion) && styles.bulkDeleteActionButtonDisabled,
                    ]}
                    onPress={handleOpenBulkDeleteConfirmation}
                    disabled={selectedQuestionCount === 0 || isDeletingAnyQuestion}
                    accessibilityRole="button"
                    aria-label={`Delete selected questions (${selectedQuestionCount})`}>
                    <TrashIcon size={14} color={colors.white} />
                    <CustomText
                      text={`Delete (${selectedQuestionCount})`}
                      useTranslationText={false}
                      size="s"
                      style={styles.bulkDeleteActionButtonText}
                      weight="normal"
                    />
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <View style={styles.summaryPill}>
                <CustomText
                  text={questionBankSummaryText}
                  size="s"
                  useTranslationText={false}
                  style={styles.summaryText}
                  weight="normal"
                />
              </View>
            )}
            <>
              <CustomSpacing size="s" />
              <View style={styles.paginationRow}>
                <CustomButton
                  text="Prev"
                  useTranslationText={false}
                  size="s"
                  styleType="primaryTransparentStroke"
                  leftIcon={null}
                  rightIcon={null}
                  onPress={() => {
                    void handlePreviousPage();
                  }}
                  disabled={isLoading || !canLoadPreviousPage}
                />
                <CustomText
                  text={`Page ${currentPage} of ${totalPages}`}
                  size="s"
                  useTranslationText={false}
                  style={styles.paginationText}
                  weight="normal"
                />
                <CustomButton
                  text="Next"
                  useTranslationText={false}
                  size="s"
                  styleType="primaryTransparentStroke"
                  leftIcon={null}
                  rightIcon={null}
                  onPress={() => {
                    void handleNextPage();
                  }}
                  disabled={isLoading || !canLoadNextPage}
                />
              </View>
            </>

            {isMcqAssessment ? (
              currentMcqQuestions.length === 0 ? (
                <CustomText
                  text="No MCQ questions found for this assessment."
                  useTranslationText={false}
                  style={styles.emptyText}
                />
              ) : (
                <>
                  {currentMcqQuestions.map((question, idx) => {
                    const isSelected = selectedQuestionIds.includes(question.id ?? "");

                    return (
                      <View key={question.id ?? `mcq-${idx}`} style={styles.questionCard}>
                        <View style={styles.questionCardHeader}>
                          <View style={styles.questionHeaderTopRow}>
                            <View style={styles.questionHeaderLabelGroup}>
                              {canManageQuestions && (
                                <TouchableOpacity
                                  style={[
                                    styles.selectionActionButton,
                                    isSelected && styles.selectionActionButtonActive,
                                  ]}
                                  onPress={() => {
                                    if (question.id) {
                                      toggleQuestionSelection(question.id);
                                    }
                                  }}
                                  disabled={isDeletingAnyQuestion || !question.id}
                                  accessibilityRole="checkbox"
                                  accessibilityState={{ checked: isSelected }}
                                  aria-label={`Select question ${idx + 1}`}>
                                  {isSelected && <CheckIcon size={12} color={colors.white} weight="bold" />}
                                </TouchableOpacity>
                              )}
                              <CustomText
                                text={`QUESTION ${idx + 1}`}
                                size="xs"
                                useTranslationText={false}
                                style={styles.questionIndex}
                                weight="normal"
                              />
                            </View>
                            {canManageQuestions && (
                              <View style={styles.questionActions}>
                                <TouchableOpacity
                                  style={styles.questionActionButton}
                                  onPress={() => handleOpenEditQuestionModal(question)}
                                  disabled={isDeletingAnyQuestion}
                                  accessibilityRole="button"
                                  aria-label={`Edit question ${idx + 1}`}>
                                  <NotePencilIcon size={16} color={colors.grey[500]} />
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={styles.questionActionButton}
                                  onPress={() => {
                                    if (question.id) {
                                      void handleDeleteQuestion(AssessmentType.Mcq, question.id);
                                    }
                                  }}
                                  disabled={isDeletingAnyQuestion}
                                  accessibilityRole="button"
                                  aria-label={`Delete question ${idx + 1}`}>
                                  <TrashIcon size={16} color={colors.grey[500]} />
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>
                          <CustomText
                            text={question.question ?? ""}
                            size="m"
                            useTranslationText={false}
                            style={styles.questionTitle}
                            weight="normal"
                          />
                        </View>

                        <View style={styles.questionCardBody}>
                          <CustomText
                            text="OPTIONS"
                            size="xs"
                            useTranslationText={false}
                            style={styles.sectionLabel}
                            weight="normal"
                          />
                          <CustomSpacing size="s" />

                          <View style={styles.rubricsWrap}>
                            {(question.choices ?? []).map((choice, choiceIndex) => {
                              const correctIndices =
                                (question as { correctChoiceIndices?: number[] }).correctChoiceIndices ?? [];
                              const isCorrect = correctIndices.includes(choiceIndex);

                              return (
                                <View
                                  key={`${question.id ?? idx}-${choiceIndex}`}
                                  style={[styles.choiceRow, isCorrect && styles.correctChoiceRow]}>
                                  <CustomText
                                    text={`${optionLabel(choiceIndex)}.`}
                                    size="s"
                                    useTranslationText={false}
                                    style={[styles.choiceIndex, isCorrect && styles.correctChoiceIndex]}
                                    weight="normal"
                                  />
                                  <CustomText
                                    text={choice}
                                    size="s"
                                    useTranslationText={false}
                                    style={styles.choiceText}
                                    weight="normal"
                                  />
                                </View>
                              );
                            })}
                          </View>

                          {!!question.explanation && (
                            <>
                              <CustomSpacing size="m" />
                              <CustomText
                                text="EXPLANATION"
                                size="xs"
                                useTranslationText={false}
                                style={styles.sectionLabel}
                                weight="normal"
                              />
                              <CustomSpacing size="xs" />
                              <CustomText
                                text={question.explanation}
                                size="s"
                                useTranslationText={false}
                                style={styles.explanationText}
                                weight="normal"
                              />
                            </>
                          )}

                          <CustomSpacing size="m" />
                          <View style={styles.totalRow}>
                            <CustomText
                              text={`Total score: ${question.totalScore ?? 0}`}
                              size="s"
                              useTranslationText={false}
                              style={styles.totalText}
                              weight="normal"
                            />
                          </View>
                        </View>

                        <View style={styles.questionCardFooter}>
                          <CustomText
                            text="Source: Assessment Question Bank"
                            size="s"
                            useTranslationText={false}
                            style={styles.footerText}
                            weight="normal"
                          />
                        </View>
                      </View>
                    );
                  })}
                </>
              )
            ) : flattenedSocraticQuestions.length === 0 ? (
              <CustomText
                text="No socratic questions found for this assessment."
                useTranslationText={false}
                style={styles.emptyText}
              />
            ) : (
              flattenedSocraticQuestions.map(({ tutor, question }, idx) => {
                const isSelected = selectedQuestionIds.includes(question.id ?? "");
                const socraticQuestion = question;
                const rubrics = socraticQuestion.rubrics ?? [];
                const totalPoints =
                  typeof socraticQuestion.totalScore === "number"
                    ? socraticQuestion.totalScore
                    : rubrics.reduce((s, r) => s + (typeof r.points === "number" ? r.points : 0), 0);

                return (
                  <View key={question.id ?? `${tutor.id}-${idx}`} style={styles.questionCard}>
                    <View style={styles.questionCardHeader}>
                      <View style={styles.questionHeaderTopRow}>
                        <View style={styles.questionHeaderLabelGroup}>
                          {canManageQuestions && (
                            <TouchableOpacity
                              style={[styles.selectionActionButton, isSelected && styles.selectionActionButtonActive]}
                              onPress={() => {
                                if (question.id) {
                                  toggleQuestionSelection(question.id);
                                }
                              }}
                              disabled={isDeletingAnyQuestion || !question.id}
                              accessibilityRole="checkbox"
                              accessibilityState={{ checked: isSelected }}
                              aria-label={`Select question ${idx + 1}`}>
                              {isSelected && <CheckIcon size={12} color={colors.white} weight="bold" />}
                            </TouchableOpacity>
                          )}
                          <CustomText
                            text={`QUESTION ${idx + 1}`}
                            size="xs"
                            useTranslationText={false}
                            style={styles.questionIndex}
                            weight="normal"
                          />
                        </View>
                        {canManageQuestions && (
                          <View style={styles.questionActions}>
                            <TouchableOpacity
                              style={styles.questionActionButton}
                              onPress={() => handleOpenEditSocraticQuestionModal(socraticQuestion, tutor)}
                              disabled={isDeletingAnyQuestion}
                              accessibilityRole="button"
                              aria-label={`Edit question ${idx + 1}`}>
                              <NotePencilIcon size={16} color={colors.grey[500]} />
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={styles.questionActionButton}
                              onPress={() => {
                                if (question.id) {
                                  void handleDeleteQuestion(AssessmentType.SocraticDialog, question.id);
                                }
                              }}
                              disabled={isDeletingAnyQuestion}
                              accessibilityRole="button"
                              aria-label={`Delete question ${idx + 1}`}>
                              <TrashIcon size={16} color={colors.grey[500]} />
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                      <CustomText
                        text={socraticQuestion.question ?? ""}
                        size="m"
                        useTranslationText={false}
                        style={styles.questionTitle}
                        weight="normal"
                      />
                    </View>

                    <View style={styles.questionCardBody}>
                      <CustomText
                        text="RUBRIC CRITERIA"
                        size="xs"
                        useTranslationText={false}
                        style={styles.sectionLabel}
                        weight="normal"
                      />
                      <CustomSpacing size="s" />

                      {rubrics.length === 0 ? (
                        <CustomText
                          text="No rubrics available for this question."
                          useTranslationText={false}
                          style={styles.emptyText}
                        />
                      ) : (
                        <View style={styles.rubricsWrap}>
                          {rubrics.map((r) => (
                            <View key={r.id} style={styles.rubricRow}>
                              <CustomText
                                text={r.component ?? ""}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricText}
                                weight="normal"
                              />
                              <CustomText
                                text={`${r.points ?? 0} pts`}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricPoints}
                                weight="normal"
                              />
                            </View>
                          ))}
                        </View>
                      )}

                      <CustomSpacing size="m" />
                      <View style={styles.totalRow}>
                        <CustomText
                          text={`Total: ${totalPoints} pts`}
                          size="s"
                          useTranslationText={false}
                          style={styles.totalText}
                          weight="normal"
                        />
                      </View>
                    </View>

                    <View style={styles.questionCardFooter}>
                      <CustomText
                        text={`Source: ${tutor.title ?? "Interactive Learning Experience"}`}
                        size="s"
                        useTranslationText={false}
                        style={styles.footerText}
                        weight="normal"
                      />
                    </View>
                  </View>
                );
              })
            )}
          </>
        )}
      </FormLayout>

      <DeleteQuestionConfirmationModal
        visible={pendingDeleteAction !== null}
        deleteCount={pendingDeleteAction?.questionIds.length ?? 0}
        onClose={closeDeleteConfirmation}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        isDeleting={isDeletingAnyQuestion}
      />

      <McqQuestionEditorModal
        visible={isQuestionEditorOpen && editorType === "mcq"}
        mode={editorMode}
        aiTutors={currentAiTutors}
        selectedAiTutorId={mcqEditorSelectedAiTutorId ?? currentAiTutors[0]?.id ?? null}
        assessmentId={assessmentId}
        question={mcqQuestionForEditor}
        nextOrder={nextQuestionOrder}
        onClose={closeQuestionEditor}
        onSaved={async () => {
          await onRefresh?.();
        }}
      />
      <SocraticQuestionEditorModal
        visible={isQuestionEditorOpen && editorType === "socratic"}
        mode={editorMode}
        aiTutors={currentAiTutors}
        assessmentId={assessmentId}
        selectedAiTutorId={editingSocraticQuestionAiTutorId}
        question={socraticQuestionForEditor}
        onClose={closeQuestionEditor}
        onSaved={async () => {
          await onRefresh?.();
        }}
      />
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  errorText: {
    color: colors.error[600],
  },
  emptyText: {
    color: colors.grey[500],
  },
  loading: {
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryPill: {
    backgroundColor: colors.grey[50],
    borderRadius: radii.lg,
    paddingHorizontal: 14,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.grey[100],
    marginBottom: 14,
  },
  summaryText: {
    color: colors.grey[700],
  },
  selectionSummaryPill: {
    backgroundColor: colors.brand[50],
    borderRadius: radii.lg,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: colors.brand[200],
    marginBottom: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
  },
  selectionSummaryText: {
    color: colors.brand[900],
  },
  selectionSummaryActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  paginationRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  paginationText: {
    color: colors.grey[700],
    flex: 1,
    textAlign: "center",
  },
  questionCard: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    overflow: "hidden",
    marginBottom: 14,
    backgroundColor: colors.white,
  },
  questionCardHeader: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 14,
    backgroundColor: colors.grey[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  questionHeaderTopRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  questionHeaderLabelGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  questionActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  questionActionButton: {
    width: 28,
    height: 28,
    borderRadius: radii.md,
    alignItems: "center",
    justifyContent: "center",
  },
  selectionActionButton: {
    width: 18,
    height: 18,
    borderRadius: radii.sm,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.white,
  },
  selectionActionButtonActive: {
    borderColor: colors.brand[500],
    backgroundColor: colors.brand[500],
  },
  questionIndex: {
    color: colors.grey[500],
    letterSpacing: 0.6,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: spacing.xs,
  },
  bulkDeleteActionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderRadius: radii.sm,
    backgroundColor: colors.error[600],
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  bulkDeleteActionButtonDisabled: {
    opacity: 0.5,
  },
  bulkDeleteActionButtonText: {
    color: colors.white,
  },
  questionTitle: {
    color: colors.grey[900],
    marginTop: 6,
    lineHeight: 22,
    fontSize: 16,
  },
  questionCardBody: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 14,
  },
  rubricsWrap: {
    gap: spacing.xs,
  },
  sectionLabel: {
    color: colors.grey[500],
    letterSpacing: 0.6,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    backgroundColor: colors.brand[50],
    borderRadius: radii.lg,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  rubricText: {
    color: colors.grey[900],
    flex: 1,
  },
  rubricPoints: {
    color: colors.brand[500],
  },
  choiceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    backgroundColor: colors.grey[50],
    borderRadius: radii.lg,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  correctChoiceRow: {
    backgroundColor: colors.success[50],
    borderColor: colors.success[200],
  },
  choiceIndex: {
    color: colors.grey[500],
  },
  correctChoiceIndex: {
    color: colors.success[600],
  },
  choiceText: {
    color: colors.grey[900],
    flex: 1,
  },
  explanationText: {
    color: colors.grey[700],
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  totalText: {
    color: colors.grey[700],
  },
  questionCardFooter: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.grey[50],
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  footerText: {
    color: colors.grey[700],
  },
});

export default AssessmentQuestionBankModal;
